import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { Link } from 'react-router-dom';

const regionCards = [
  {
    name: 'San Francisco Bay Area',
    tagline: 'Major Health Systems & Academic Centers',
    image: 'https://public.readdy.ai/ai/img_res/1f233e984118bcc0d109507560847aa7.jpg',
    modalities: ['MRI', 'CT', 'Ultrasound', 'X-Ray'],
  },
  {
    name: 'Greater Los Angeles',
    tagline: 'Largest Coverage Zone in Southern California',
    image: 'https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Los%20Angeles%20downtown%20skyline%20at%20golden%20hour%20with%20modern%20buildings%20and%20mountains%20in%20background%20showing%20expansive%20urban%20healthcare%20landscape%20professional%20photography&width=800&height=600&seq=coverage-region-la-001&orientation=landscape',
    modalities: ['MRI', 'CT', 'Ultrasound', 'X-Ray'],
  },
  {
    name: 'Central Valley',
    tagline: 'Supporting Rural & Agricultural Communities',
    image: 'https://readdy.ai/api/search-image?query=Scenic%20California%20Central%20Valley%20landscape%20with%20green%20agricultural%20fields%20and%20distant%20Sierra%20Nevada%20mountains%20at%20sunrise%20showing%20vast%20open%20terrain%20and%20rural%20community%20setting%20professional%20photography&width=800&height=600&seq=coverage-region-valley-001&orientation=landscape',
    modalities: ['MRI', 'CT', 'X-Ray'],
  },
  {
    name: 'San Diego Region',
    tagline: 'Coastal Healthcare Infrastructure',
    image: 'https://readdy.ai/api/search-image?query=San%20Diego%20California%20coastline%20and%20city%20skyline%20at%20sunset%20with%20Coronado%20Bridge%20and%20harbor%20showing%20modern%20coastal%20urban%20landscape%20with%20warm%20tones%20professional%20photography&width=800&height=600&seq=coverage-region-sd-001&orientation=landscape',
    modalities: ['MRI', 'CT', 'Ultrasound'],
  },
  {
    name: 'Sacramento Metro',
    tagline: 'State Capital Healthcare Network',
    image: 'https://readdy.ai/api/search-image?query=Sacramento%20California%20state%20capitol%20building%20and%20downtown%20skyline%20with%20Tower%20Bridge%20and%20Sacramento%20River%20at%20golden%20hour%20showing%20government%20district%20and%20urban%20landscape%20professional%20photography&width=800&height=600&seq=coverage-region-sac-001&orientation=landscape',
    modalities: ['MRI', 'CT', 'Ultrasound', 'X-Ray'],
  },
  {
    name: 'Central Coast',
    tagline: 'Santa Barbara to Monterey Corridor',
    image: 'https://readdy.ai/api/search-image?query=California%20Central%20Coast%20scenic%20view%20with%20Pacific%20Ocean%20coastline%20and%20rolling%20green%20hills%20near%20Santa%20Barbara%20at%20sunset%20showing%20beautiful%20coastal%20landscape%20professional%20photography&width=800&height=600&seq=coverage-region-coast-001&orientation=landscape',
    modalities: ['MRI', 'CT'],
  },
];

export default function RegionCards() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.05 });

  return (
    <section ref={ref} className="py-16 sm:py-24 px-4 sm:px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div
          className={`text-center mb-12 sm:mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-slate-blue text-[13px] uppercase tracking-[2px] font-medium mb-3">
            FEATURED REGIONS
          </p>
          <h2 className="text-navy text-[26px] sm:text-[36px] lg:text-[42px] font-semibold leading-tight mb-4">
            Where We Operate
          </h2>
          <p className="text-[#5A6C7D] text-base sm:text-lg max-w-2xl mx-auto">
            From the Bay Area to San Diego, our fleet is positioned for rapid deployment to any healthcare facility in California.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {regionCards.map((region, index) => (
            <div
              key={index}
              className={`group relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="relative h-[260px] sm:h-[300px] overflow-hidden">
                <img
                  src={region.image}
                  alt={`${region.name} coverage area`}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>

                {/* Content overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-5 h-5 flex items-center justify-center text-white/80">
                      <i className="ri-map-pin-2-fill text-sm"></i>
                    </span>
                    <span className="text-white/70 text-xs font-medium uppercase tracking-wider">
                      Service Region
                    </span>
                  </div>
                  <h3 className="text-white text-lg sm:text-xl font-semibold mb-1">{region.name}</h3>
                  <p className="text-white/65 text-sm mb-3">{region.tagline}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {region.modalities.map((mod) => (
                      <span
                        key={mod}
                        className="bg-white/15 backdrop-blur-sm text-white text-[11px] px-2.5 py-1 rounded-full font-medium"
                      >
                        {mod}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div
          className={`text-center mt-12 transition-all duration-700 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-[#5A6C7D] text-sm mb-4">
            Don&apos;t see your area? We may still be able to serve your facility.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-navy text-white rounded-full font-semibold text-[15px] hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            Check Your Coverage
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-line text-base"></i>
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}
